import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import FinancialProductRulesEditDetailsOutputDataTable from './FinancialProductRulesEditDetailsOutputDataTable';
import { REMOVE_FINANCIAL_PRODUCT_RULE_OUTPUT_DATA, ADD_UPDATE_FINANCIAL_PRODUCT_RULE_OUTPUT_DATA } from '../../../mutations';
import { FINANCIAL_PRODUCT_RULE_OUTPUT_DATA_REMOVED, FINANCIAL_PRODUCT_RULE_OUTPUT_DATA_UPDATED } from '../../../subscriptions';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';
import { getInt } from 'utils/functions';
import { useRouteMatch } from 'react-router-dom';
import { FINANCIAL_PRODUCT_RULE_OUTPUT_DATA_PARAM_LIST_QUERY } from '../../../queries';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { financialProductRuleOutputDataValidator } from '../../../validators';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { remove } from 'ramda';
import { emptyArray } from 'utils/constants';
import { emptyString } from 'utils/constants';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';

const FinancialProductRulesEditDetailsOutputDataContainer = ({ outputDataList, isSaved, canWrite }) => {
  const addToast = useToast();
  const [t] = useTranslation();
  const match = useRouteMatch();
  const ruleId = match.params.ruleId && getInt(match.params.ruleId);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(emptyArray);
  const [financialProductRuleOutputDataValidation, validateFinancialProductRuleOutputData] = useDirtyFieldValidation(
    financialProductRuleOutputDataValidator
  );

  const { data: financialProductRuleOutputDataParamList } = useQuery(FINANCIAL_PRODUCT_RULE_OUTPUT_DATA_PARAM_LIST_QUERY);

  useEffect(() => {
    resetLens(
      outputDataList?.map(element => {
        const elementLocal = { ...element };
        elementLocal.isInEditing = false;
        elementLocal.isSaving = false;
        elementLocal.paramName = financialProductRuleOutputDataParamList?.financialProductRuleOutputDataParamList.find(
          y => y.paramId === element.paramId
        )?.paramName;
        return elementLocal;
      }) || []
    );
  }, [resetLens, outputDataList, financialProductRuleOutputDataParamList]);

  useEffect(() => {
    validateFinancialProductRuleOutputData(lens |> get, dirtyInfo);
  }, [dirtyInfo, lens, validateFinancialProductRuleOutputData]);

  const handleAdd = useCallback(() => {
    set(lens)([
      ...(lens |> get),
      {
        financialProductRuleOutputDataId: null,
        readOnlyYN: false,
        paramId: null,
        paramName: emptyString,
        defaultValue: 0,
        minValue: 0,
        maxValue: 0,
        isInEditing: true
      }
    ]);
  }, [lens]);

  const handleCloseEdit = useCallback(
    orderNo => {
      const lensLocal = [...(lens |> get)];
      if (lensLocal[orderNo].financialProductRuleOutputDataId === null) {
        lensLocal.splice(orderNo, 1);
      } else {
        const unmodifiedElement = outputDataList.find(
          el => el.financialProductRuleOutputDataId === lensLocal[orderNo].financialProductRuleOutputDataId
        );
        lensLocal[orderNo] = { ...unmodifiedElement, isInEditing: false };
      }
      set(lens)(lensLocal);
    },
    [lens, outputDataList]
  );

  const [saveFinancialProductRuleOutputData] = useMutation(ADD_UPDATE_FINANCIAL_PRODUCT_RULE_OUTPUT_DATA);

  const handleSave = useCallback(
    index => {
      (lens[index].isSaving |> set)(true);
      const itemToSave = lens[index] |> get;
      saveFinancialProductRuleOutputData({
        variables: {
          input: {
            financialProductRuleOutputDataId: parseInt(itemToSave.financialProductRuleOutputDataId),
            financialProductRuleId: ruleId,
            readOnlyYN: itemToSave.readOnlyYN,
            paramId: itemToSave.paramId,
            defaultValue: parseFloat(itemToSave.defaultValue),
            minValue: parseFloat(itemToSave.minValue),
            maxValue: parseFloat(itemToSave.maxValue)
          }
        }
      });
    },
    [lens, ruleId, saveFinancialProductRuleOutputData]
  );

  useSubscription(FINANCIAL_PRODUCT_RULE_OUTPUT_DATA_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('FinancialProduct.Details.Rules.Details.OutputData.FinancialProductRuleOutputDataSaved'), 'success');
      client.cache.modify({
        id: `FinancialProductRuleEditItem:{"financialProductRuleId":${ruleId}}`,
        fields: {
          outputData: (previous, { toReference }) => {
            if (
              (lens |> get).some(
                x =>
                  x.financialProductRuleOutputDataId ===
                  data.financialProductRuleOutputDataUpdated.outputData.financialProductRuleOutputDataId
              )
            )
              return previous;
            else return [...previous, toReference(data.financialProductRuleOutputDataUpdated.outputData)];
          }
        }
      });
    }
  });

  const [deleteFinancialProductRuleOutputData] = useMutation(REMOVE_FINANCIAL_PRODUCT_RULE_OUTPUT_DATA);

  const handleDelete = useCallback(
    (outputDataId, index) => {
      if (!outputDataId) set(lens)(remove(index, 1, lens |> get));
      else if (window.confirm(t('FinancialProduct.Details.Rules.Details.OutputData.DeleteFinancialProductRuleOutputData'))) {
        deleteFinancialProductRuleOutputData({
          variables: {
            input: { outputDataId }
          }
        });
      }
    },
    [deleteFinancialProductRuleOutputData, lens, t]
  );

  useSubscription(FINANCIAL_PRODUCT_RULE_OUTPUT_DATA_REMOVED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('FinancialProduct.Details.Rules.Details.OutputData.FinancialProductRuleOutputDataRemoved'), 'success');
      client.cache.evict({
        id: `FinancialProductRuleEditOutputDataItem:{"financialProductRuleOutputDataId":${data.financialProductRuleOutputDataDeleted.outputDataId}}`
      });
      client.cache.gc();
    }
  });

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useGlobalIsDirty(isDirty);

  return (
    <>
      <FinancialProductRulesEditDetailsOutputDataTable
        canWrite={canWrite}
        dirtyInfo={dirtyInfo}
        outputDataList={lens}
        financialProductRuleOutputDataParamList={
          financialProductRuleOutputDataParamList?.financialProductRuleOutputDataParamList ?? emptyArray
        }
        isSaved={isSaved}
        onAdd={handleAdd}
        onCloseEdit={handleCloseEdit}
        onDelete={handleDelete}
        onSave={handleSave}
        financialProductRuleOutputDataValidation={financialProductRuleOutputDataValidation}
      />
    </>
  );
};

FinancialProductRulesEditDetailsOutputDataContainer.propTypes = {
  outputDataList: PropTypes.array.isRequired,
  isSaved: PropTypes.bool.isRequired,
  canWrite: PropTypes.bool.isRequired
};

export default FinancialProductRulesEditDetailsOutputDataContainer;
